// C.1 — Стратег Ли Пул (global_li_pul), архетип «Мобильность».
//
// xlsx v6 dsl_trigger1:
//   event: turn_start
//   target: any_unit_within_own_side
//   effect: move
//   value: any_free_cell_within_own_side
//   → В начале своего хода: игрок выбирает ЛЮБОЙ свой юнит и перемещает
//     его на ЛЮБУЮ свободную ячейку своей стороны. Можно пропустить (skip).
//     Picker как в старой версии Стратега Ли Пула.
//
// xlsx v6 dsl_active (Ф2, 1× за матч):
//   target: self
//   effect: freeze
//   value: 2
//   → 2 хода без атак (freeze всей фазы). Используем swarmFrozenUntilTurn.

import { registerImplementedAbility } from '../../warn-unimplemented.js';
import { moveUnit } from '../../../move-unit.js';

export const HERO_ID = 'global_li_pul';

// === Trigger ===
//
// state.players[idx].liPulTrigger = {
//   pending: true
// }
// UI/бот вызывает applyLiPulMove(state, playerIdx, { unitInstanceId, destLine, destSlot })
// или skipLiPulTrigger(state, playerIdx) для пропуска.

export function liPulOnTurnStart(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return { ok: false, reason: 'wrong_hero' };
  if (p.hero.current?.silenced) return { ok: false, reason: 'silenced' };

  // Есть ли вообще юниты на поле и свободная ячейка?
  let hasUnit = false;
  let hasFreeSlot = false;
  for (let line = 0; line < 2; line++) {
    const row = p.field?.[line] || [];
    for (const s of row) {
      if (s) hasUnit = true;
      else hasFreeSlot = true;
    }
  }
  if (!hasUnit || !hasFreeSlot) return { ok: false, reason: 'no_move_possible' };

  p.liPulTrigger = { pending: true };
  state.log?.add(`🪙 Ли Пул [${p.name}]: триггер — можно переместить юнит (или пропустить)`);
  return { ok: true, needsPicker: true };
}

// Кандидаты для UI: юниты на поле + свободные ячейки.
export function getLiPulMoveCandidates(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.field) return { units: [], freeSlots: [] };
  const units = [];
  const freeSlots = [];
  for (let line = 0; line < 2; line++) {
    const row = p.field[line] || [];
    for (let s = 0; s < row.length; s++) {
      if (row[s]) units.push({ instanceId: row[s].instanceId, name_ru: row[s].name_ru, line, slot: s });
      else freeSlots.push({ line, slot: s });
    }
  }
  return { units, freeSlots };
}

// Применить перемещение.
//   opts.unitInstanceId — кого двигаем
//   opts.destLine, opts.destSlot — куда
export function applyLiPulMove(state, playerIdx, opts = {}) {
  const p = state.players?.[playerIdx];
  if (!p?.liPulTrigger?.pending) return { ok: false, reason: 'no_pending_trigger' };

  const { unitInstanceId, destLine, destSlot } = opts;
  if (unitInstanceId == null || destLine == null || destSlot == null) {
    return { ok: false, reason: 'missing_args' };
  }

  // Найти юнит.
  let unit = null;
  for (const row of p.field || []) {
    for (const u of row || []) {
      if (u?.instanceId === unitInstanceId) { unit = u; break; }
    }
    if (unit) break;
  }
  if (!unit) return { ok: false, reason: 'unit_not_found' };

  const result = moveUnit(state, unit, destLine, destSlot);
  if (!result?.ok) return { ok: false, reason: result?.reason || 'move_failed' };

  p.liPulTrigger = null;
  state.log?.add(`🪙 Ли Пул: ${unit.name_ru} перемещён на (${destLine + 1}, ${destSlot + 1})`);
  return { ok: true };
}

// Пропустить триггер (skip).
export function skipLiPulTrigger(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.liPulTrigger) return { ok: false, reason: 'no_pending_trigger' };
  p.liPulTrigger = null;
  state.log?.add(`🪙 Ли Пул: триггер пропущен`);
  return { ok: true };
}

// Авто-разрешение для бота: пропускаем (бот не строит сложных передвижений
// в C.1). Можно усилить эвристикой позже.
export function autoResolveLiPulTrigger(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.liPulTrigger?.pending) return;
  // Простая эвристика: пропускаем.
  skipLiPulTrigger(state, playerIdx);
}

// === Активка Ф2: 2 хода без атак ===
//
// «2 хода без атак» — используем существующий механизм swarmFrozenUntilTurn
// (старая активка Стратега). Заморозка обоих игроков ИЛИ только нашей
// стороны? По диздоку — глобальная заморозка (свои не атакуют + чужие).

export function liPulActive(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return { ok: false, reason: 'wrong_hero' };
  if (p.heroActiveUsedThisMatch) return { ok: false, reason: 'already_used' };

  // Глобальная заморозка фаз атак на 2 хода: устанавливаем
  // state.swarmFrozenUntilTurn = currentTurn + 2.
  const currentTurn = state.currentTurn ?? 1;
  state.swarmFrozenUntilTurn = Math.max(state.swarmFrozenUntilTurn || 0, currentTurn + 2);

  p.heroActiveUsedThisMatch = true;
  state.log?.add(`🪙 Ли Пул [${p.name}]: активка — фазы атак заморожены до ${currentTurn + 2} хода`);
  return { ok: true };
}

export function liPulActiveAvailable(state, playerIdx) {
  const p = state.players?.[playerIdx];
  if (!p?.hero || p.hero.id !== HERO_ID) return false;
  if (p.heroActiveUsedThisMatch) return false;
  if (p.hero.current?.silenced) return false;
  return true;
}

registerImplementedAbility(HERO_ID, 'trigger', 'active');
